import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KINDS = [
  { id: "",          label: "everything" },
  { id: "journals",  label: "journals" },
  { id: "preprints", label: "preprints" },
  { id: "textbooks", label: "textbooks" },
  { id: "courses",   label: "courses" },
  { id: "search",    label: "searches" },
  { id: "news",      label: "news" },
  { id: "data",      label: "data" },
  { id: "reference", label: "reference" },
];

const KIND_LABEL = {
  journals:  "peer-reviewed journals",
  preprints: "preprint servers",
  textbooks: "open textbooks",
  courses:   "public college curricula + free ecourses",
  search:    "internet searches",
  news:      "news media outlets",
  data:      "public primary data + open databases",
  reference: "reference archives",
};

/**
 * BrainPage — the free education portal. One search box. No login, no
 * cookies, no tracking. Federates DOAJ (real journal-article hits) plus a
 * curated card list for every other free source.
 *
 * Founder's Message-194 · Nail 15 · Human Knowledge Portal.
 */
export default function BrainPage() {
  const location = useLocation();
  const initialKind = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const k = (params.get("kind") || "").toLowerCase();
    const allowed = ["journals", "preprints", "textbooks", "courses", "search", "news", "data", "reference"];
    return allowed.includes(k) ? k : "";
  }, [location.search]);

  const [query, setQuery] = useState("");
  const [kind, setKind] = useState(initialKind);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null); // { doaj_articles, sources, ... }
  const [sourcesShelf, setSourcesShelf] = useState(null);
  const inputRef = useRef(null);

  // Re-sync the filter whenever the URL query flips (dropdown clicks between pages).
  useEffect(() => {
    setKind(initialKind);
  }, [initialKind]);

  useEffect(() => {
    // Preload the full curated shelf so a first-time visitor sees the map
    // of every source even before they type.
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/brain/sources`);
        if (r.ok) setSourcesShelf(await r.json());
      } catch {}
    })();
    // Focus the search box.
    setTimeout(() => inputRef.current?.focus?.(), 60);
  }, []);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (q.length < 2) {
      setError("Type at least two characters.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const url = new URL(`${BACKEND_URL}/api/brain/search`);
      url.searchParams.set("q", q);
      if (kind) url.searchParams.set("kind", kind);
      const r = await fetch(url.toString());
      if (!r.ok) throw new Error(await r.text());
      setPayload(await r.json());
    } catch (err) {
      setError(err.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const groupedSources = useMemo(() => {
    const list = payload?.sources || sourcesShelf?.sources || [];
    // If a kind filter is picked, only show doors of that kind. This makes
    // every dropdown tab in The Brain actually change what's on the page.
    const filtered = kind ? list.filter((s) => s.kind === kind) : list;
    const byKind = {};
    filtered.forEach((s) => {
      byKind[s.kind] = byKind[s.kind] || [];
      byKind[s.kind].push(s);
    });
    return byKind;
  }, [payload, sourcesShelf, kind]);

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain">
      <TopNav />
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-1">The Brain</div>
          <div className="flex items-start gap-3 mt-1">
            <div className="text-sage-accent shrink-0"><ProductMark slug="brain" size={44} /></div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-sage-text">
              Free forever. No login. No tracking.
            </h1>
          </div>
          <p className="text-sm text-sage-muted leading-relaxed sage-prose mt-2 max-w-2xl">
            One search box across free scientific publications, open textbooks, public college curricula, primary public data, and reference archives. Sage sends you to the source. Sage never counts who you are or what you asked.
          </p>
        </div>

        {/* Search box */}
        <form onSubmit={runSearch} className="border border-sage-border p-4 mb-6" data-testid="brain-search-form">
          <div className="flex gap-2 items-center mb-3">
            <input
              ref={inputRef}
              data-testid="brain-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask anything — climate change, quantum mechanics, homelessness policy, Roman history…"
              className="flex-1 bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="brain-search-submit"
              className="text-xs uppercase tracking-[0.1em] border border-sage-accent text-sage-accent px-4 py-2 hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-40"
            >
              {loading ? "Searching…" : "Search →"}
            </button>
          </div>
          <div className="flex flex-wrap gap-1" data-testid="brain-kind-filter">
            {KINDS.map((k) => (
              <button
                key={k.id}
                type="button"
                onClick={() => setKind(k.id)}
                data-testid={`brain-kind-${k.id || "all"}`}
                className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${kind === k.id ? "border-sage-accent text-sage-accent" : "border-sage-border text-sage-muted hover:border-sage-text hover:text-sage-text"}`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </form>

        {error && (
          <div className="border border-sage-warn p-3 mb-4 text-xs text-sage-warn" data-testid="brain-search-error">
            {error}
          </div>
        )}

        {/* DOAJ real hits */}
        {payload && payload.doaj_articles && payload.doaj_articles.length > 0 && (
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint mb-3">
              Peer-reviewed articles · DOAJ · {payload.doaj_count} of many
            </div>
            <div className="space-y-3" data-testid="brain-doaj-results">
              {payload.doaj_articles.map((a, i) => (
                <a
                  key={i}
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`brain-doaj-${i}`}
                  className="block border border-sage-border p-3 hover:border-sage-accent transition-colors"
                >
                  <div className="text-sm text-sage-text font-bold leading-snug">{a.title}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mt-1 tabular-nums">
                    {a.journal ? `${a.journal} · ` : ""}{a.year || ""}{a.authors ? ` · ${a.authors}` : ""}
                  </div>
                  {a.abstract && (
                    <div className="text-xs text-sage-muted mt-2 leading-relaxed sage-prose">
                      {a.abstract.length > 300 ? a.abstract.slice(0, 300) + "…" : a.abstract}
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        {payload && payload.doaj_articles && payload.doaj_articles.length === 0 && (
          <div className="mb-6 text-xs text-sage-muted italic">
            DOAJ returned no direct article hits for "{payload.query}". Try one of the source doors below — each opens its own search page for the same query.
          </div>
        )}

        {/* Source cards — either search-filled (post-query) or curated shelf (initial). */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint mb-3">
            {payload
              ? `Every free source · your query loaded into each door · ${payload.sources_count} doors`
              : (kind
                  ? `${KIND_LABEL[kind] || kind} · ${Object.values(groupedSources).reduce((n, arr) => n + arr.length, 0)} doors`
                  : `Every free source on the shelf · ${sourcesShelf?.count || 0} doors`)}
          </div>
          <div className="space-y-5" data-testid="brain-sources">
            {Object.keys(groupedSources).sort().map((k) => (
              <div key={k}>
                <div className="text-xs uppercase tracking-[0.15em] text-sage-text mb-2">
                  {KIND_LABEL[k] || k} <span className="text-sage-faint">({groupedSources[k].length})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {groupedSources[k].map((s) => (
                    <a
                      key={s.id}
                      href={s.search_url || s.home}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`brain-source-${s.id}`}
                      className="border border-sage-border p-3 hover:border-sage-accent transition-colors"
                    >
                      <div className="text-sm text-sage-text">{s.name}</div>
                      {s.note && <div className="text-[10px] text-sage-faint mt-1">{s.note}</div>}
                      <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent mt-2">
                        {payload ? "search this source →" : "visit source →"}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 border-t border-sage-border pt-4 text-xs text-sage-faint sage-prose">
          Founder's law · This door never asks who you are. Never logs what you searched. Never sells a byte of your query. If it ever does, that's a bug — file it on the Ledger.
        </div>
      </div>
    </div>
  );
}
